/**
 * TSR realtime protocol — the socket events (and the sepak takraw REST reads)
 * the TSR scoreboard speaks to the API. Ported from the predecessor backend
 * (Wsl-Hono-BE `src/socket/tsr/core`) with the event names, rooms and payload
 * shapes UNCHANGED, so the TSR client's socket code ports as it is.
 *
 * ── Two wire conventions, on purpose ────────────────────────────────────────
 * Socket payloads keep v1's camelCase (`meetId`, `rev`, `serverId`): they are
 * the TSR client's contract and the user's hard requirement is that it ports
 * unchanged (the TLC singular-controller socket does the same). The REST reads
 * go through the API's `ok()` envelope and are snake_case like every other
 * HTTP response (CLAUDE.md "Naming").
 *
 * ── Nothing is written to Postgres while a match is live ────────────────────
 * Live state lives ONLY in Redis (`match_state:<meetId>`, 24 h; stats
 * `match_stats:<meetId>`, 3 days). The result is saved when the operator
 * presses Save Meet: the TSR client maps its final state with
 * `tsrSetsSaveBody` (./sets-mapping.ts) and calls the EXISTING
 * `PATCH /api/meets/:id/results` as the signed-in operator — the group /
 * sport / permission check of that route applies. There is no server-side
 * persistence path for live scores.
 *
 * ── Auth ────────────────────────────────────────────────────────────────────
 * The handshake never refuses (anonymous displays and kiosks must connect).
 * Reads — `join_*`, `leave_*`, `get_*` — are open. Every state-changing TSR
 * event (any event starting `tsr_`) needs an identity whose active role may
 * write results for the meet's sport (`SOCKET_WRITE_AUTH`, default
 * `enforce`), re-checked per packet against the token's `exp` and the
 * denylist. A refused write acks `{ error: TSR_AUTH_REQUIRED }` (no or dead
 * identity — the client's sync chip keys on that exact text) or
 * `{ error: TSR_WRITE_FORBIDDEN }`; an emit without an ack gets `auth_error`.
 * A socket outlives its 15-minute token: send `auth_refresh { token }` after
 * refreshing, or reconnect.
 */

import { z } from "zod";
import type { TsrBowlingMatchStateEnvelope } from "./bowling-mapping";

export type TsrTeam = "A" | "B";

/** Error strings a refused TSR write acks with. `TSR_AUTH_REQUIRED` is matched verbatim by the v1 client. */
export const TSR_AUTH_REQUIRED = "Authentication required";
export const TSR_WRITE_FORBIDDEN = "Not allowed to score this meet";

/** Live state TTLs (seconds) — `match_state:<meetId>` and `match_stats:<meetId>`. */
export const TSR_MATCH_STATE_TTL_SECONDS = 24 * 60 * 60;
export const TSR_MATCH_STATS_TTL_SECONDS = 3 * 24 * 60 * 60;
/** The largest `tsr_match_state` / `tsr_match_stats` payload accepted (JSON bytes). */
export const TSR_PAYLOAD_MAX_BYTES = 256 * 1024;

/** `sports.code` of sepak takraw (SEAG and APG seeds) — what `/api/tsr/sepak-takraw` serves. */
export const TSR_SEPAK_TAKRAW_SPORT_CODE = "STK";

/**
 * The set / rally sports with a live scoreboard read at `/api/tsr/<slug>`
 * (v1 served `/tsr/badminton` and `/tsr/sepak-takraw`; every set sport
 * streams the same `tsr_match_state`, so each prefix is the same router
 * filtered to its sport). Keyed by slug — the TSR app's sport slug.
 */
export const TSR_SET_SPORT_FEEDS = {
	"sepak-takraw": { sportCodes: ["STK"], label: "sepak takraw" },
	badminton: { sportCodes: ["BD"], label: "badminton" },
	"table-tennis": { sportCodes: ["TTE"], label: "table tennis" },
	tennis: { sportCodes: ["TEN"], label: "tennis" },
	squash: { sportCodes: ["SQU"], label: "squash" },
	volleyball: { sportCodes: ["VBL"], label: "volleyball" },
	"lawn-bowls": { sportCodes: ["LBW"], label: "lawn bowls" },
} as const satisfies Record<string, { sportCodes: readonly string[]; label: string }>;
export type TsrSetSportSlug = keyof typeof TSR_SET_SPORT_FEEDS;

/** Stream Deck / remote actions the sepak takraw controller applies (`sepak_takraw_action`). */
export const TSR_SEPAK_TAKRAW_REMOTE_ACTIONS = ["POINT"] as const;
export type TsrSepakTakrawRemoteAction = (typeof TSR_SEPAK_TAKRAW_REMOTE_ACTIONS)[number];

// ─── The state the scoreboard streams ────────────────────────────────────────

export interface TsrCompletedSet {
	a: number;
	b: number;
	winner: TsrTeam | null;
	/** Tennis tie-break only. */
	tbLoserPoints?: number;
	/** Tennis: this set was a match (super) tie-break — its score is points (10–7). */
	superTiebreak?: boolean;
}

/** A game of a multi-game team tie (Team Regu) already played out. */
export interface TsrCompletedGame {
	game: number;
	sets: TsrCompletedSet[];
	setsWonA: number;
	setsWonB: number;
	winner: TsrTeam | null;
}

/**
 * The part of TSR's `LiveMatchState` (WSL-TSR-X scoreboard.engine.ts) the
 * server reads. Structural: TSR ships many more fields (clocks, cards, line-
 * ups, display view); they are stored and relayed verbatim, never rejected.
 */
export interface TsrLiveMatchState {
	maxSets: number;
	maxGames: number;
	multiGame: boolean;
	/** 1-based; maxSets + 1 once the last set is done. */
	currentSet: number;
	completedSets: TsrCompletedSet[];
	/** The set in progress. */
	scoreA: number;
	scoreB: number;
	setsWonA: number;
	setsWonB: number;
	currentGame: number;
	gameWinsA: number;
	gameWinsB: number;
	gameHistory?: TsrCompletedGame[];
	servingTeam: TsrTeam;
	matchFinished: boolean;
	tennisScoring?: boolean;
	pointDisplayA?: string;
	pointDisplayB?: string;
	timeoutActive?: boolean;
	timeoutTeam?: TsrTeam | null;
	/** Timed engines. */
	timeoutRemaining?: number;
	/** Set-based engine (sepak takraw). Same value as `timeoutRemaining`. */
	timeoutClock?: number;
	/** Time-outs SPENT per side. */
	timeoutsA?: number;
	timeoutsB?: number;
	[field: string]: unknown;
}

export interface TsrMatchStateEnvelope {
	meetId: string;
	/** Monotonic per controller; the server drops a packet whose rev ≤ the one it holds. */
	rev: number;
	state: TsrLiveMatchState;
}

/** One logged point of the Stats Controller (TSR `MatchStatsState.events[]`), read defensively. */
export interface TsrStatsEvent {
	id?: string;
	/** The side CREDITED with the point. An error action is the opponent's fault. */
	team: TsrTeam;
	/** `TSR_SEPAK_TAKRAW_STATS_ACTIONS` key: ace, spike, service-fault… */
	action: string;
	detail?: string | null;
	player?: string | null;
	playerTeam?: TsrTeam | null;
	/** Court position 0..1 (x along the length, A's half < 0.5). */
	x?: number;
	y?: number;
	set?: number;
	game?: number;
	scoreA?: number;
	scoreB?: number;
	/** Epoch ms. */
	at?: number;
}

/** TSR's `MatchStatsState`, stored verbatim. Only `events` is read. */
export interface TsrMatchStats {
	events?: TsrStatsEvent[];
	[field: string]: unknown;
}

export interface TsrStatsAction {
	key: string;
	label: string;
	short: string;
	/** winner — the credited side's own play; error — given away by the opponent. */
	kind: "winner" | "error";
}

/** Mirror of WSL-TSR-X `sepak-takraw.stats.ts`. An unknown key counts as a winner, like TSR. */
export const TSR_SEPAK_TAKRAW_STATS_ACTIONS: readonly TsrStatsAction[] = [
	{ key: "ace", label: "Service Ace", short: "ACE", kind: "winner" },
	{ key: "spike", label: "Spike", short: "SPK", kind: "winner" },
	{ key: "block", label: "Block", short: "BLK", kind: "winner" },
	{ key: "feint", label: "Feint / Tap", short: "FNT", kind: "winner" },
	{ key: "header", label: "Header", short: "HDR", kind: "winner" },
	{ key: "winner-other", label: "Other Winner", short: "WIN", kind: "winner" },
	{ key: "service-fault", label: "Service Fault", short: "SF", kind: "error" },
	{ key: "attack-error", label: "Attack Error", short: "AE", kind: "error" },
	{ key: "net-fault", label: "Net / Touch Fault", short: "NF", kind: "error" },
	{ key: "error-other", label: "Other Fault", short: "FLT", kind: "error" },
];

// ─── Projections pushed to consumers ─────────────────────────────────────────

export type TsrMatchFeedStatus = "upcoming" | "live" | "finished";

/**
 * The stable scoreboard feed — `match_feed_updated`'s payload, and (snake_cased,
 * plus `sides`) the REST `/scoreboard`. Insulated from TSR's internal state.
 */
export interface TsrMatchFeed {
	meetId: string;
	rev: number;
	status: TsrMatchFeedStatus;
	format: { multiGame: boolean; maxGames: number; maxSets: number };
	currentGame: number;
	currentSet: number;
	serving: TsrTeam;
	finished: boolean;
	winner: TsrTeam | null;
	score: {
		/** The set in progress; null once finished. */
		current: { a: number; b: number } | null;
		sets: { no: number; a: number; b: number; winner: TsrTeam | null }[];
		/** Sets won (single game) or games won (team tie). */
		won: { a: number; b: number };
	};
	timeout: { active: boolean; team: TsrTeam | null; secondsLeft: number | null; used: { a: number | null; b: number | null } };
	/** Tennis only. */
	points?: { a: string; b: string };
}

/** What a station points at (`join_tsr_station` ack, `tsr_station_meet_changed`). */
export interface TsrStationSelection {
	meetId: string;
	eventId: string;
	updatedAt: string;
}

export type TsrBoardStatus = TsrMatchFeedStatus;

/** `tsr_board_announce` — "this controller window is running this meet". Sent on mount and every ~15 s. */
export interface TsrBoardAnnounce {
	meetId: string;
	/** `/scoreboard/<slug>/display` — what a Multi-View tile loads. */
	sportSlug: string;
	eventId?: string;
	meetNo?: string;
	eventName?: string;
	stage?: string;
	court?: string;
	teamA?: string;
	teamB?: string;
	/** Normally omitted: the server derives it from accepted state packets. */
	status?: TsrBoardStatus;
}

export interface TsrLiveBoard {
	meetId: string;
	sportSlug: string;
	eventId: string;
	meetNo: string;
	eventName: string;
	stage: string;
	court: string;
	teamA: string;
	teamB: string;
	status: TsrBoardStatus;
	/** When the court first came up — the grid's order. */
	announcedAt: string;
	lastActiveAt: string;
}

// ─── Acks ────────────────────────────────────────────────────────────────────

/** Every failure ack: `{ error }`. The v1 client reads `ack.success` then `ack.error`. */
export interface TsrAckError {
	success?: false;
	error: string;
}

export interface TsrJoinMatchAck {
	success: true;
	/** `TSR_SERVER_ID` — which server this socket reached (cloud vs a venue box). */
	serverId: string;
	/** Sockets in the meet room, this one included. */
	peers: number;
	rev: number;
	state: TsrLiveMatchState | null;
	/** The snapshot could not be READ (Redis down): keep your own state. */
	degraded: boolean;
}

/** `stale: true` — the server holds a newer rev; nothing was stored or relayed. */
export type TsrWriteAck = { success: true; stale: true } | { success: true; degraded: boolean };

export interface TsrJoinMatchFeedAck {
	success: true;
	meetId: string;
	feed: TsrMatchFeed | null;
	degraded: boolean;
}

export interface TsrGetMatchStatsAck {
	success: true;
	rev: number;
	stats: TsrMatchStats | null;
	degraded: boolean;
}

export interface TsrJoinStationAck {
	success: true;
	stationId: string;
	selection: TsrStationSelection | null;
}

export interface TsrSetStationMeetAck {
	success: true;
	selection: TsrStationSelection;
	/** Broadcast, but not stored: windows opened later will not pick it up on their own. */
	degraded: boolean;
}

// ─── The event maps (socket.io typed-events style) ───────────────────────────

type Ack<T> = (response: T | TsrAckError) => void;

/**
 * Client → server. Rooms: `meet_<meetId>` (one per socket — joining another
 * meet leaves the previous one), `match_feed_<meetId>`, `tsr_station_<id>`
 * (one per socket), `tsr_boards`, `sepak-takraw:<meetId>`.
 */
export interface TsrClientToServerEvents {
	// Reads — open to anonymous sockets.
	join_match: (data: { meetId: string }, ack?: Ack<TsrJoinMatchAck>) => void;
	leave_match: (data: { meetId: string }) => void;
	join_match_feed: (data: { meetId: string }, ack?: Ack<TsrJoinMatchFeedAck>) => void;
	leave_match_feed: (data: { meetId: string }, ack?: (r: { success: true }) => void) => void;
	get_match_stats: (data: { meetId: string }, ack: Ack<TsrGetMatchStatsAck>) => void;
	join_tsr_station: (data: { stationId: string }, ack?: Ack<TsrJoinStationAck>) => void;
	join_tsr_boards: (data: unknown, ack?: Ack<{ success: true; boards: TsrLiveBoard[] }>) => void;
	leave_tsr_boards: (data: unknown, ack?: (r: { success: true }) => void) => void;
	join_sepak_takraw: (data: { meetId: string }, ack?: (r: { success: true; room: string }) => void) => void;
	/** Swap the socket's identity for a refreshed access token; `{ success: false }` keeps the old one. */
	auth_refresh: (data: { token: string }, ack?: (r: { success: boolean; error?: string }) => void) => void;

	// Writes — identity + `canWriteMeet` (SOCKET_WRITE_AUTH).
	tsr_match_state: (data: TsrMatchStateEnvelope, ack?: Ack<TsrWriteAck>) => void;
	tsr_match_stats: (data: { meetId: string; rev: number; stats: TsrMatchStats }, ack?: Ack<TsrWriteAck>) => void;
	/** Tenpin bowling's frame state (`TsrBowlingMatchState`) — same key, rooms, rev rule and acks as `tsr_match_state`. */
	tsr_bowling_match_state: (data: TsrBowlingMatchStateEnvelope, ack?: Ack<TsrWriteAck>) => void;
	tsr_station_set_meet: (data: { stationId: string; meetId: string; eventId?: string }, ack?: Ack<TsrSetStationMeetAck>) => void;
	tsr_board_announce: (data: TsrBoardAnnounce, ack?: Ack<{ success: true }>) => void;
	/** Only the current announcer's socket removes the court. Identity required, not a meet grant. */
	tsr_board_gone: (data: { meetId: string }, ack?: Ack<{ success: true }>) => void;
}

/** Server → client. */
export interface TsrServerToClientEvents {
	/** Relayed to the meet room (not the sender) on every accepted packet. */
	tsr_match_state: (data: TsrMatchStateEnvelope) => void;
	tsr_match_stats: (data: { meetId: string; rev: number; stats: TsrMatchStats }) => void;
	/** Relayed to the meet room (not the sender) on every accepted bowling packet. */
	tsr_bowling_match_state: (data: TsrBowlingMatchStateEnvelope) => void;
	/**
	 * The OVR projection of an accepted packet whose score moved, one per side.
	 * `participantId` is the slot's `meet_competitor_id` (A = first by
	 * start_order). A key the projection dropped (an undone set) is re-sent "".
	 */
	participant_scores_updated: (data: {
		meetId: string;
		participantId: string;
		results: { scores: Record<string, string | boolean>; status?: "LIVE" };
		timestamp: string;
	}) => void;
	/** Volatile, to `match_feed_<meetId>` only. */
	match_feed_updated: (data: TsrMatchFeed) => void;
	tsr_station_meet_changed: (data: { stationId: string; selection: TsrStationSelection }) => void;
	tsr_boards_updated: (data: { boards: TsrLiveBoard[] }) => void;
	/** To `sepak-takraw:<meetId>` — from `POST /api/tsr/sepak-takraw/point`. */
	sepak_takraw_action: (data: { meetId: string; action: { type: TsrSepakTakrawRemoteAction; team: TsrTeam } }) => void;
	/** A refused write that carried no ack. */
	auth_error: (data: { event: string; error: string }) => void;
}

// ─── REST: /api/tsr/sepak-takraw (snake_case, `ok()` envelope) ───────────────

/** One side of the meet as the new schema names it. A = the first filled slot by start_order. */
export interface TsrMeetSide {
	meet_competitor_id: string;
	competitor_id: string;
	side: string | null;
	start_order: number | null;
	/** The competitor's display name (`competitorNameSql`). */
	name: string | null;
	/** IOC code of the organisation's country (MAS, THA); null without one. */
	noc_code: string | null;
	organization_name: string | null;
	flag_url: string | null;
	members: { competitor_member_id: string; people_id: string; printed_name: string; role: string; sort_order: number }[];
}

export interface TsrMeetSides {
	a: TsrMeetSide;
	b: TsrMeetSide;
}

/** `GET /live`: sepak takraw meets that streamed this process, newest first. Not paginated: bounded by the courts running now. */
export interface TsrSepakTakrawLiveResponse {
	meets: {
		meet_id: string;
		rev: number;
		status: TsrMatchFeedStatus;
		updated_at: string;
		meet_no: string;
		event_code: string;
		event_name: string;
		sides: TsrMeetSides | null;
	}[];
	count: number;
}

/**
 * `GET /:meetId/state`: the stored envelope, snake_cased, + `sides`.
 * Data-keyed maps (`cardLevels`, `athleteNames`) come as `[{ key, value }]`.
 */
export interface TsrSepakTakrawStateResponse {
	meet_id: string;
	rev: number;
	state: Record<string, unknown>;
	sides: TsrMeetSides | null;
}

/** The same `/live`, `/:id/state`, `/:id/scoreboard` and `/:id/stats` for every `TSR_SET_SPORT_FEEDS` prefix. */
export type TsrSetSportLiveResponse = TsrSepakTakrawLiveResponse;
export type TsrSetSportStateResponse = TsrSepakTakrawStateResponse;

/** `GET /:meetId/scoreboard`: `TsrMatchFeed`, snake_cased, + `sides`. */
export type TsrSepakTakrawScoreboardResponse = Record<string, unknown> & { sides: TsrMeetSides | null };

/** `GET /:meetId/stats[?scope=set]`: the stats projection, snake_cased, + `scoreboard` (or null). */
export type TsrSepakTakrawStatsResponse = Record<string, unknown> & {
	sides: TsrMeetSides | null;
	scoreboard: Record<string, unknown> | null;
};

export const tsrStatsQuery = z.object({ scope: z.enum(["match", "set"]).default("match") }).strict();

/** `POST /point` body (Stream Deck). Needs a SERVICE device token of kind STREAM_DECK, scoped to the sport. */
export const tsrSepakTakrawPointBody = z
	.object({
		meet_id: z.string().uuid(),
		team: z.enum(["A", "B"]),
	})
	.strict();
export type TsrSepakTakrawPointBody = z.input<typeof tsrSepakTakrawPointBody>;

export interface TsrSepakTakrawPointResponse {
	meet_id: string;
	team: TsrTeam;
	action: TsrSepakTakrawRemoteAction;
	/** Sockets in the controller room the action reached (0 = no controller open). */
	delivered_to: number;
}

export type TsrSetSportScoreboardResponse = TsrSepakTakrawScoreboardResponse;
export type TsrSetSportStatsResponse = TsrSepakTakrawStatsResponse;

// The Save Meet mappers (TSR live state → `PATCH /meets/:id/results`) ship with the protocol.
export * from "./sets-mapping";
export * from "./bowling-mapping";
